import {
  ComponentPropsWithoutRef,
  forwardRef,
  useEffect,
  useRef,
  useState,
  MouseEvent,
  useCallback,
  useLayoutEffect,
} from "react";
import { useLayoutPropsUtil } from "../../hooks";
import cx from "classnames";
import styles from "./Overflow.module.scss";
import { OverflowText } from "./OverflowText";
import { Flex, LayoutUtilProps } from "../Flex";
import { getKeyboardFocusableElements } from "../../functions";

/**
 * Props for the Overflow component
 * @extends ComponentPropsWithoutRef<"div">
 * @extends Omit<LayoutUtilProps, "direction">
 */
export type OverflowProps = ComponentPropsWithoutRef<"div"> &
  Omit<LayoutUtilProps, "direction"> & {
    /**
     * The direction of the overflow container
     * @default column
     */
    direction?: LayoutUtilProps["flexDirection"];
    /**
     * The height of the overflow container
     * @remarks "inherit" is not supported when truncate is enabled
     */
    height?: number | string | "inherit";
    /**
     * The width of the overflow container
     */
    width?: number | string;
  } & (
    | {
        /**
         * Whether to truncate content with expand/collapse functionality
         * @default false
         */
        truncate?: false;
      }
    | {
        /**
         * Direction must be column when truncate is enabled
         */
        direction: "column";
        /**
         * Truncation configuration with expand/collapse functionality
         */
        truncate:
          | boolean
          | {
              /**
               * Text to display on the expand button
               * @default "Show more..."
               */
              expandText?: string;
              /**
               * Text to display on the collapse button
               * @default "Show less..."
               */
              collapseText?: string;
              /**
               * Callback fired when content is expanded
               */
              onExpand?: (e: MouseEvent<HTMLButtonElement>) => void;
              /**
               * Callback fired when content is collapsed
               */
              onCollapse?: (e: MouseEvent<HTMLButtonElement>) => void;
            };
      }
  );

/**
 * Internal state for shadow indicators
 */
type ShadowState = {
  /**
   * Whether to show right shadow indicator
   */
  right: boolean;
  /**
   * Whether to show left shadow indicator
   */
  left: boolean;
  /**
   * Whether to show top shadow indicator
   */
  top: boolean;
  /**
   * Whether to show bottom shadow indicator
   */
  bottom: boolean;
};

const OverflowElement = forwardRef<HTMLDivElement, OverflowProps>(
  (props, ref) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      children,
      className,
      direction = "column",
      style,
      height,
      width,
      truncate,
      gap,
      ...rest
    } = componentProps;
    const contentRef = useRef<HTMLDivElement>(null);
    const [expanded, setExpanded] = useState(false);
    const [shadow, setShadow] = useState<ShadowState>({
      left: false,
      right: false,
      top: false,
      bottom: false,
    });
    const [contentClipped, setContentClipped] = useState(false);

    const shadowUpdate = useCallback(() => {
      if (!contentRef.current) return;
      if (direction === "row") {
        const right =
          contentRef.current.offsetWidth < contentRef.current.scrollWidth;
        setShadow((prev) => {
          return {
            ...prev,
            right,
          };
        });
        return;
      }
      const bottom =
        contentRef.current.offsetHeight < contentRef.current.scrollHeight;
      setShadow((prev) => {
        return {
          ...prev,
          bottom,
        };
      });
    }, [direction]);

    useLayoutEffect(() => {
      if (!contentRef.current) return;

      const mutationCallback = () => {
        if (!contentRef.current) return;
        const contentHeight =
          typeof height === "number" ? height : contentRef.current.offsetHeight;
        if (contentHeight < contentRef.current.scrollHeight) {
          setContentClipped(true);
          return;
        }
        setExpanded(false);
        setContentClipped(false);
      };

      const observer = new MutationObserver(mutationCallback);
      observer.observe(contentRef.current, {
        childList: true, // Observe changes to child elements
      });
      return () => {
        observer.disconnect();
      };
    }, []);

    useLayoutEffect(() => {
      if (contentRef.current) {
        const observer = new ResizeObserver(() => {
          shadowUpdate();
        });

        observer.observe(contentRef.current);

        // Cleanup function
        return () => {
          observer.disconnect();
        };
      }
    }, [shadowUpdate]);

    useEffect(() => {
      if (!contentRef.current) return;
      setContentClipped(
        contentRef.current.offsetHeight < contentRef.current.scrollHeight,
      );
    }, []);

    // update overflow position on scroll
    const handleOnScroll = () => {
      if (!contentRef.current) return;
      const target = contentRef.current;
      if (direction === "row") {
        const left = target.scrollLeft > 0;
        const right =
          target.scrollLeft < target.scrollWidth - target.offsetWidth;
        setShadow({
          left,
          right,
          top: false,
          bottom: false,
        });
        return;
      }
      const top = target.scrollTop > 0;
      const bottom =
        target.scrollTop < target.scrollHeight - target.offsetHeight;
      setShadow({
        top,
        bottom,
        left: false,
        right: false,
      });
    };
    const inertOverflowFocusables = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((element) => {
        if (!element.isIntersecting) {
          element.target.setAttribute("disabled", "true");
        } else {
          element.target.removeAttribute("disabled");
        }
      });
    };

    const overflowClassNames = cx([styles["overflow"]], className, {
      [styles["vertical"]]: direction === "column",
      [styles["horizontal"]]: direction === "row",
      [styles["truncate"]]: !!truncate,
      [styles["expanded"]]: expanded,
    });

    useEffect(() => {
      if (!truncate) return;
      const observer = new IntersectionObserver(inertOverflowFocusables);
      const focusable = getKeyboardFocusableElements(contentRef.current);
      focusable.forEach((element) => {
        observer.observe(element);
      });
    }, [truncate]);

    if (truncate) {
      const wrapperStyle = {
        ...style,
        ...layoutStyles,
      };
      const contentStyle = {
        minHeight: height ?? undefined,
        minWidth: width ?? undefined,
      };

      const expandString =
        typeof truncate === "object" ? truncate.expandText : "Show more...";
      const collapseString =
        typeof truncate === "object" ? truncate.collapseText : "Show less...";

      const onExpandClickHandler = (e: MouseEvent<HTMLButtonElement>) => {
        setExpanded((prev) => !prev);
        setTimeout(() => {
          handleOnScroll();
        }, 100);
        if (typeof truncate !== "object") return;
        if (expanded) {
          truncate.onCollapse?.(e);
          return;
        }
        truncate.onExpand?.(e);
      };

      const showExpand = contentClipped;

      return (
        <div
          ref={ref}
          style={wrapperStyle}
          className={overflowClassNames}
          data-anv="overflow"
          {...rest}
        >
          {shadow.top && !truncate ? (
            <div className={styles["shadow-top"]} />
          ) : null}
          {shadow.left && !truncate ? (
            <div className={styles["shadow-left"]} />
          ) : null}
          <Flex
            className={styles["content"]}
            onScroll={handleOnScroll}
            style={contentStyle}
            ref={contentRef}
            direction={direction}
            gap={gap}
            /**
             * This is disabled because scrollable area much also be focusable.
             * References:
             * - {@link https://github.com/jsx-eslint/eslint-plugin-jsx-a11y/blob/0d5321a5457c5f0da0ca216053cc5b4f571b53ae/docs/rules/no-noninteractive-tabindex.md}
             * - {@link https://chromestatus.com/feature/5231964663578624}
             */
            tabIndex={0}
          >
            {children}
          </Flex>
          {shadow.bottom && !truncate ? (
            <div className={styles["shadow-bottom"]} />
          ) : null}
          {shadow.right && !truncate ? (
            <div className={styles["shadow-right"]} />
          ) : null}
          {truncate && showExpand ? (
            <button
              className={styles["expand-button"]}
              onClick={onExpandClickHandler}
            >
              {expanded ? collapseString : expandString}
            </button>
          ) : null}
        </div>
      );
    }

    const wrapperStyle = {
      ...style,
      ...layoutStyles,
    };

    const contentStyle = {
      height: height ?? undefined,
      width: width ?? undefined,
    };

    return (
      <div
        ref={ref}
        style={wrapperStyle}
        className={overflowClassNames}
        data-anv="overflow"
        {...rest}
      >
        {shadow.left ? <div className={styles["shadow-left"]} /> : null}
        {shadow.top ? <div className={styles["shadow-top"]} /> : null}
        <Flex
          className={styles["content"]}
          onScroll={handleOnScroll}
          ref={contentRef}
          style={contentStyle}
          direction={direction}
          gap={gap}
          /**
           * This is disabled because scrollable area much also be focusable.
           * References:
           * - {@link https://github.com/jsx-eslint/eslint-plugin-jsx-a11y/blob/0d5321a5457c5f0da0ca216053cc5b4f571b53ae/docs/rules/no-noninteractive-tabindex.md}
           * - {@link https://chromestatus.com/feature/5231964663578624}
           */
          tabIndex={0}
        >
          {children}
        </Flex>
        {shadow.bottom ? <div className={styles["shadow-bottom"]} /> : null}
        {shadow.right ? <div className={styles["shadow-right"]} /> : null}
      </div>
    );
  },
);

OverflowElement.displayName = "Overflow";

/**
 * Overflow component for handling content that exceeds container boundaries.
 *
 * Features:
 * - Supports both horizontal and vertical overflow
 * - Automatic shadow indicators for scrollable content
 * - Truncation with expand/collapse functionality
 * - Keyboard navigation support for focusable elements
 * - Automatic height and width management
 * - Layout utility props support
 * - Resize observer for dynamic content changes
 * - Accessibility features with proper focus management
 * - Scroll event handling with shadow updates
 *
 * @example
 * <Overflow direction="column" height={400} gap="4">
 *   {items.map((item, i) => (
 *     <Card key={i} style={{ width: "100%", minHeight: 100 }}>
 *       {item.content}
 *     </Card>
 *   ))}
 * </Overflow>
 */
export const Overflow = Object.assign(OverflowElement, {
  /**
   * OverflowText component for text content with line truncation and expansion.
   *
   * Features:
   * - Configurable line limit with row-based truncation
   * - Expandable content with custom expand/collapse text
   * - Automatic height calculation and overflow detection
   * - Keyboard navigation support for focusable elements
   * - Resize observer for dynamic content changes
   * - Accessibility features with proper focus management
   * - Support for unlimited rows or specific row limits
   * - Callback support for expand/collapse events
   *
   * @example
   * <Overflow.Text rows={3} expandable>
   *   <Text>
   *     This is a long text that will be truncated to 3 rows
   *     and can be expanded to show the full content.
   *   </Text>
   * </Overflow.Text>
   */
  Text: OverflowText,
});
